import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Shield, User, Check, X, Trash2, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Nemaš pristup");
    }
  };
  useEffect(() => { load(); }, []);

  const approve = async (u) => {
    try {
      await api.post(`/users/${u.id}/approve`);
      toast.success(`${u.name} odobren`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reject = async (u) => {
    if (!window.confirm(`Odbiti registraciju: ${u.name}?`)) return;
    try {
      await api.post(`/users/${u.id}/reject`);
      toast.success(`${u.name} odbijen`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleRole = async (u) => {
    const next = u.role === "admin" ? "member" : "admin";
    try {
      await api.put(`/users/${u.id}/role`, { role: next });
      toast.success(`${u.name}: ${next === "admin" ? "postavljen kao admin" : "vraćen na člana"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Obrisati korisnika ${u.name} i sve njegove podatke? Ova akcija je NEPOVRATNA.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Korisnik obrisan");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (err) {
    return (
      <div className="surface-card p-8 text-center">
        <Shield className="w-10 h-10 mx-auto text-ink-muted" />
        <div className="mt-3 font-heading text-lg">Nedostupno</div>
        <div className="text-sm text-ink-muted mt-1">{err}</div>
      </div>
    );
  }

  const pending = users.filter((u) => (u.status || "active") === "pending");
  const active = users.filter((u) => (u.status || "active") === "active");
  const rejected = users.filter((u) => (u.status || "active") === "rejected");

  return (
    <div className="space-y-6">
      <div>
        <div className="overline">Admin</div>
        <h1 className="mt-1 text-3xl md:text-4xl font-heading font-semibold tracking-tight">Korisnici</h1>
        <p className="text-ink-muted mt-2">Odobri nove zahteve, upravljaj admin pristupom, obriši naloge.</p>
      </div>

      {pending.length > 0 && (
        <section data-testid="pending-section">
          <div className="overline mb-2 flex items-center gap-1.5"><Clock className="w-3 h-3" /> Zahtevi za registraciju ({pending.length})</div>
          <div className="surface-card divide-y divide-line" style={{ borderColor: "#D99A5B" }}>
            {pending.map((u) => (
              <div key={u.id} className="p-4 flex items-center gap-4" data-testid={`pending-row-${u.id}`}>
                <div className="w-10 h-10 rounded-md bg-surface-secondary border border-line grid place-items-center text-ink-muted">
                  <User className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{u.name}</div>
                  <div className="text-xs text-ink-muted truncate">{u.email}</div>
                </div>
                <button data-testid={`approve-${u.id}`} onClick={() => approve(u)} className="btn-primary !py-1.5">
                  <Check className="w-4 h-4" /> Odobri
                </button>
                <button data-testid={`reject-${u.id}`} onClick={() => reject(u)} className="btn-ghost !py-1.5 !text-terracotta">
                  <X className="w-4 h-4" /> Odbij
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="overline mb-2">Aktivni korisnici ({active.length})</div>
        <div className="surface-card divide-y divide-line">
          {active.map((u) => (
            <div key={u.id} className="p-4 flex items-center gap-4" data-testid={`user-row-${u.id}`}>
              <div className="w-10 h-10 rounded-md bg-surface-secondary border border-line grid place-items-center text-ink-muted">
                <User className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{u.name} {u.id === me?.id && <span className="text-xs text-ink-muted">(ti)</span>}</div>
                <div className="text-xs text-ink-muted truncate">{u.email}</div>
              </div>
              <span
                className={`text-xs px-2 py-1 rounded border font-medium ${
                  u.role === "admin" ? "bg-forest text-white border-forest" : "bg-surface-secondary text-ink border-line"
                }`}
                data-testid={`user-role-${u.id}`}
              >
                {u.role === "admin" ? "Admin" : "Član"}
              </span>
              <button className="btn-ghost !py-1.5" onClick={() => toggleRole(u)} disabled={u.id === me?.id} data-testid={`user-toggle-${u.id}`}>
                {u.role === "admin" ? "Skini admin" : "Postavi kao admin"}
              </button>
              <button className="btn-ghost !py-1.5 !text-terracotta" onClick={() => remove(u)} disabled={u.id === me?.id} data-testid={`user-delete-${u.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </section>

      {rejected.length > 0 && (
        <section>
          <div className="overline mb-2">Odbijeni ({rejected.length})</div>
          <div className="surface-card divide-y divide-line">
            {rejected.map((u) => (
              <div key={u.id} className="p-4 flex items-center gap-4 opacity-60" data-testid={`rejected-row-${u.id}`}>
                <div className="w-10 h-10 rounded-md bg-surface-secondary border border-line grid place-items-center text-ink-muted">
                  <User className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{u.name}</div>
                  <div className="text-xs text-ink-muted truncate">{u.email}</div>
                </div>
                <button className="btn-ghost !py-1.5" onClick={() => approve(u)} data-testid={`reapprove-${u.id}`}>
                  <Check className="w-4 h-4" /> Vrati aktivnog
                </button>
                <button className="btn-ghost !py-1.5 !text-terracotta" onClick={() => remove(u)} data-testid={`user-delete-${u.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
